import { motion, AnimatePresence } from "motion/react";
import { X, Download, Printer, Share2, Package2, CheckCircle, TrendingUp } from "lucide-react";
import { ImageWithFallback } from "./figma/ImageWithFallback";

interface CubeTestReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: {
    batchId: string;
    grade: string;
    plant: string;
    timestamp: string;
    cubeTests: Array<{ day: number; strength: number; status: string }>;
    currentStrength: number;
    requiredStrength: number;
    strengthUnit: string;
    beforeImage: string;
    afterImage: string;
    customerName?: string;
    projectName?: string;
    siteLocation?: string;
  };
}

export default function CubeTestReportModal({
  isOpen,
  onClose,
  data,
}: CubeTestReportModalProps) {
  const isPassing = data.currentStrength >= data.requiredStrength;
  const compliance = ((data.currentStrength / data.requiredStrength) * 100).toFixed(1);

  const handleDownload = () => {
    console.log("Downloading cube test report PDF...");
  };

  const handlePrint = () => {
    window.print();
  };

  const handleShare = () => {
    console.log("Sharing report...");
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            onClick={onClose}
          />

          {/* Bottom Sheet Modal */}
          <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center md:p-4 pointer-events-none pb-16 md:pb-0">
            <motion.div
              initial={{ opacity: 0, y: "100%" }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: "100%" }}
              transition={{ duration: 0.35, ease: [0.32, 0.72, 0, 1] }}
              className="bg-white rounded-t-3xl md:rounded-2xl w-full md:max-w-4xl max-h-[85vh] md:max-h-[90vh] overflow-hidden pointer-events-auto flex flex-col"
              style={{ boxShadow: "0 -10px 40px rgba(0, 0, 0, 0.15), 0 20px 50px rgba(0, 0, 0, 0.3)" }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Drag Handle (Mobile Only) */}
              <div className="md:hidden flex justify-center py-3 bg-white">
                <div className="w-10 h-1 bg-slate-300 rounded-full" />
              </div>

              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 flex-shrink-0">
                <div>
                  <h2 className="text-slate-900" style={{ fontSize: "20px", fontWeight: "700" }}>
                    Concrete Cube Test Report
                  </h2>
                  <p className="text-slate-500" style={{ fontSize: "14px" }}>
                    Compressive Strength Analysis
                  </p>
                </div>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={onClose}
                  className="p-2 hover:bg-slate-100 rounded-xl transition-colors"
                >
                  <X className="w-5 h-5 text-slate-600" />
                </motion.button>
              </div>

              {/* Scrollable Content */}
              <div className="overflow-y-auto flex-1" style={{ paddingBottom: "80px" }}>
                <div className="p-6 space-y-6">
                  {/* Header Info */}
                  <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-6 text-white">
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <div className="text-white/60 mb-1" style={{ fontSize: "12px", fontWeight: "600" }}>
                          Batch ID
                        </div>
                        <div style={{ fontSize: "20px", fontWeight: "700" }}>
                          {data.batchId}
                        </div>
                      </div>
                      <div>
                        <div className="text-white/60 mb-1" style={{ fontSize: "12px", fontWeight: "600" }}>
                          Concrete Grade
                        </div>
                        <div style={{ fontSize: "20px", fontWeight: "700" }}>
                          {data.grade}
                        </div>
                      </div>
                      <div>
                        <div className="text-white/60 mb-1" style={{ fontSize: "12px", fontWeight: "600" }}>
                          Plant Location
                        </div>
                        <div style={{ fontSize: "14px", fontWeight: "600" }}>
                          {data.plant}
                        </div>
                      </div>
                      <div>
                        <div className="text-white/60 mb-1" style={{ fontSize: "12px", fontWeight: "600" }}>
                          Casting Date
                        </div>
                        <div style={{ fontSize: "14px", fontWeight: "600" }}>
                          {data.timestamp}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Customer & Project Details */}
                  <div>
                    <h3 className="text-slate-900 mb-4" style={{ fontSize: "16px", fontWeight: "700" }}>
                      Project Information
                    </h3>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-slate-50 rounded-xl p-4">
                        <div className="text-slate-500 mb-2" style={{ fontSize: "12px", fontWeight: "600" }}>
                          Customer Name
                        </div>
                        <div className="text-slate-900" style={{ fontSize: "14px", fontWeight: "600" }}>
                          {data.customerName || "ABC Construction Pvt. Ltd."}
                        </div>
                      </div>
                      <div className="bg-slate-50 rounded-xl p-4">
                        <div className="text-slate-500 mb-2" style={{ fontSize: "12px", fontWeight: "600" }}>
                          Project Name
                        </div>
                        <div className="text-slate-900" style={{ fontSize: "14px", fontWeight: "600" }}>
                          {data.projectName || "Residential Tower - Phase 2"}
                        </div>
                      </div>
                      <div className="bg-slate-50 rounded-xl p-4">
                        <div className="text-slate-500 mb-2" style={{ fontSize: "12px", fontWeight: "600" }}>
                          Site Location
                        </div>
                        <div className="text-slate-900" style={{ fontSize: "14px", fontWeight: "600" }}>
                          {data.siteLocation || "Sector 15, Bangalore"}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Strength Results */}
                  <div>
                    <h3 className="text-slate-900 mb-4" style={{ fontSize: "16px", fontWeight: "700" }}>
                      Compressive Strength Results
                    </h3>
                    <div className={`rounded-2xl p-6 border-2 ${
                      isPassing 
                        ? "bg-gradient-to-br from-green-50 to-emerald-50 border-green-200" 
                        : "bg-gradient-to-br from-red-50 to-rose-50 border-red-200"
                    }`}>
                      <div className="flex items-center gap-3 mb-6">
                        <div className={`p-3 rounded-xl ${isPassing ? "bg-green-500" : "bg-red-500"}`}>
                          {isPassing ? (
                            <CheckCircle className="w-6 h-6 text-white" />
                          ) : (
                            <Package2 className="w-6 h-6 text-white" />
                          )}
                        </div>
                        <div>
                          <div className={`${isPassing ? "text-green-900" : "text-red-900"}`} style={{ fontSize: "18px", fontWeight: "700" }}>
                            {isPassing ? "Test Passed" : "Test Failed"}
                          </div>
                          <div className={`${isPassing ? "text-green-600" : "text-red-600"}`} style={{ fontSize: "12px" }}>
                            {compliance}% of required strength achieved
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-6 mb-6">
                        <div>
                          <div className={`mb-2 ${isPassing ? "text-green-700" : "text-red-700"}`} style={{ fontSize: "12px", fontWeight: "600" }}>
                            28-Day Strength (Actual)
                          </div>
                          <div className={`${isPassing ? "text-green-900" : "text-red-900"}`} style={{ fontSize: "40px", fontWeight: "700", letterSpacing: "-0.02em" }}>
                            {data.currentStrength}
                            <span style={{ fontSize: "20px", fontWeight: "600" }} className={`ml-2 ${isPassing ? "text-green-700" : "text-red-700"}`}>
                              {data.strengthUnit}
                            </span>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={`mb-2 ${isPassing ? "text-green-700" : "text-red-700"}`} style={{ fontSize: "12px", fontWeight: "600" }}>
                            Required Strength
                          </div>
                          <div className={`${isPassing ? "text-green-900" : "text-red-900"}`} style={{ fontSize: "32px", fontWeight: "700" }}>
                            {data.requiredStrength}
                            <span style={{ fontSize: "18px", fontWeight: "600" }} className={`ml-2 ${isPassing ? "text-green-700" : "text-red-700"}`}>
                              {data.strengthUnit}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Test Timeline */}
                      <div className="bg-white/50 backdrop-blur-sm rounded-xl p-4">
                        <div className="flex gap-2 mb-4">
                          {data.cubeTests.map((test) => (
                            <div 
                              key={test.day} 
                              className={`flex-1 h-3 rounded-full ${
                                test.status === "completed" 
                                  ? (isPassing ? "bg-green-500" : "bg-red-500")
                                  : "bg-slate-200"
                              }`}
                            />
                          ))}
                        </div>
                        <div className="flex justify-between">
                          {data.cubeTests.map((test) => (
                            <div key={test.day} className="text-center">
                              <div className={`mb-1 ${isPassing ? "text-green-900" : "text-red-900"}`} style={{ fontWeight: "700", fontSize: "16px" }}>
                                {test.status === "completed" ? `${test.strength}` : "—"}
                              </div>
                              <div className={`${isPassing ? "text-green-700" : "text-red-700"}`} style={{ fontSize: "11px", fontWeight: "600" }}>
                                Day {test.day}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Test Documentation */}
                  <div>
                    <h3 className="text-slate-900 mb-4" style={{ fontSize: "16px", fontWeight: "700" }}>
                      Test Documentation
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-slate-500 mb-2" style={{ fontSize: "12px", fontWeight: "600" }}>
                          Before Testing
                        </div>
                        <ImageWithFallback
                          src={data.beforeImage}
                          alt="Before test"
                          className="w-full h-48 object-cover rounded-xl border border-slate-200"
                        />
                      </div>
                      <div>
                        <div className="text-slate-500 mb-2" style={{ fontSize: "12px", fontWeight: "600" }}>
                          After Testing
                        </div>
                        <ImageWithFallback
                          src={data.afterImage}
                          alt="After test"
                          className="w-full h-48 object-cover rounded-xl border border-slate-200"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Detailed Results Table */}
                  <div>
                    <h3 className="text-slate-900 mb-4" style={{ fontSize: "16px", fontWeight: "700" }}>
                      Detailed Test Results
                    </h3>
                    <div className="border border-slate-200 rounded-xl overflow-hidden">
                      <table className="w-full">
                        <thead className="bg-slate-100">
                          <tr>
                            <th className="text-left p-4 text-slate-700" style={{ fontSize: "12px", fontWeight: "700" }}>
                              Test Age
                            </th>
                            <th className="text-left p-4 text-slate-700" style={{ fontSize: "12px", fontWeight: "700" }}>
                              Strength ({data.strengthUnit})
                            </th>
                            <th className="text-left p-4 text-slate-700" style={{ fontSize: "12px", fontWeight: "700" }}>
                              % of 28-Day
                            </th>
                            <th className="text-left p-4 text-slate-700" style={{ fontSize: "12px", fontWeight: "700" }}>
                              Status
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.cubeTests.map((test, index) => {
                            const percentage = ((test.strength / data.currentStrength) * 100).toFixed(0);
                            return (
                              <tr key={test.day} className={index % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                                <td className="p-4 text-slate-900" style={{ fontWeight: "600" }}>
                                  {test.day} Days
                                </td>
                                <td className="p-4 text-slate-900" style={{ fontSize: "16px", fontWeight: "700" }}>
                                  {test.strength}
                                </td>
                                <td className="p-4 text-slate-600" style={{ fontWeight: "600" }}>
                                  {percentage}%
                                </td>
                                <td className="p-4">
                                  <span className={`px-3 py-1 rounded-full ${
                                    test.status === "completed" 
                                      ? "bg-green-100 text-green-700" 
                                      : "bg-slate-200 text-slate-600"
                                  }`} style={{ fontSize: "12px", fontWeight: "600" }}>
                                    {test.status === "completed" ? "Completed" : "Pending"}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Compliance Note */}
                  <div className={`border-2 rounded-xl p-4 ${
                    isPassing 
                      ? "bg-blue-50 border-blue-200" 
                      : "bg-amber-50 border-amber-200"
                  }`}>
                    <div className={`mb-1 ${isPassing ? "text-blue-900" : "text-amber-900"}`} style={{ fontWeight: "600" }}>
                      Compliance Note
                    </div>
                    <p className={`${isPassing ? "text-blue-700" : "text-amber-700"}`} style={{ fontSize: "14px" }}>
                      {isPassing 
                        ? `This concrete batch has achieved ${data.currentStrength} ${data.strengthUnit} compressive strength at 28 days, which exceeds the required ${data.requiredStrength} ${data.strengthUnit}. The test was conducted as per IS 516:1959 standards and the concrete is approved for structural use.`
                        : `This concrete batch has achieved ${data.currentStrength} ${data.strengthUnit} compressive strength at 28 days, which is below the required ${data.requiredStrength} ${data.strengthUnit}. Further investigation and corrective measures are required.`
                      }
                    </p>
                  </div>
                </div>
              </div>

              {/* Footer Actions */}
              <div className="p-5 border-t border-slate-100 flex-shrink-0 bg-white">
                <div className="grid grid-cols-3 gap-3">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleShare}
                    className="py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl transition-all font-semibold flex items-center justify-center gap-2"
                  >
                    <Share2 className="w-4 h-4" />
                    Share
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handlePrint}
                    className="py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl transition-all font-semibold flex items-center justify-center gap-2"
                  >
                    <Printer className="w-4 h-4" />
                    Print
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleDownload}
                    className={`py-3 text-white rounded-xl transition-all font-semibold flex items-center justify-center gap-2 shadow-lg ${
                      isPassing 
                        ? "bg-green-500 hover:bg-green-600 shadow-green-500/30" 
                        : "bg-blue-500 hover:bg-blue-600 shadow-blue-500/30"
                    }`}
                  >
                    <Download className="w-4 h-4" />
                    Download PDF
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}